import React, { useState, useMemo } from "react";
import { Typography } from "@mui/material/";
import CircleIcon from "@mui/icons-material/Circle";
import { groupBy, uniq } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { strings } from "../../../../common/i18n";
import { enums, helpers } from "../../../../common/lib";
import { useAuditSummaryData } from "../../../../common/hooks";
import actions from "../../../../Store/actions";

/**
 *  Helpers and common function for close audit view and popup
 */

export default () => {
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const { data } = useAuditSummaryData();

    const {
        auditsList,
        auditAssets,
        accounts,
        assetAuditors,
        auditControllers,
        assignmentDetails,
        typesList
    } = useSelector((state) => state);

    const sumBy = (array = [], conditions) => {
        return array.reduce((count, item) => {
            const meetsConditions = Object.entries(conditions).every(
                ([key, value]) => item[key] === value
            );
            return count + (meetsConditions ? 1 : 0);
        }, 0);
    };

    /** Auditor status for close  */
    const auditorAssetStatus = useMemo(() => {
        const next = assignmentDetails.data.map((item) => {
            return {
                auditor: helpers.getListItemForId(accounts, item.auditor),
                auditorStatus: helpers.getListItemForId(
                    typesList.auditAssignmentStatus,
                    item.status
                ),
                assetSummary: {
                    auditAsset: {
                        approved: 0,
                        rejected: 0,
                        pending: 0,
                        unchecked: 0,
                        excluded: item.summary.excluded,
                        seen: item.summary.seen,
                        total: item.summary.total
                    },
                    altAsset: {
                        pending: 0,
                        resolved: 0,
                        total: 0
                    }
                }
            };
        });
        return next;
    }, [assignmentDetails, typesList]);

    /** Data required for close summary */
    const summaryData = useMemo(() => {
        const unexpectedAssets = data.filter(
            (item) => item.unexpected === true
        );
        const auditAsset = data.filter((item) => item.unexpected === false);
        const controllerIds = uniq(
            auditAssets.data.map((item) => item.updatedBy)
        );
        const auditorIds = uniq(assetAuditors.data.map((item) => item.auditor));
        const validatedVisibility = data.filter(
            (item) => item.sharedValidationAsset
        );
        let excluded = 0;
        const excludeCount = auditAsset.map((item) => {
            if (item.auditorExcluded || item.controllerExcluded) excluded++;
        });

        return {
            auditAssetUnchecked: sumBy(auditAssets.data, {
                isAssigned: false,
                isExpected: true,
                reissuedStatus: null
            }),
            auditAssetAssigned: sumBy(auditAsset, { assigned: true }),
            // auditAssetsPending: sumBy(auditAsset, { pending: true }),
            // Seen, approved/rejected and excluded are independent flags -
            // an asset can be seen AND approved AND excluded at once, and
            // should count in every slice that applies to it, not just one.
            auditAssetsSeen: sumBy(auditAsset, { seen: true }),
            auditAssetsApproved: sumBy(auditAsset, { approved: true }),
            auditAssetsRejected: sumBy(auditAsset, { rejected: true }),
            auditAssetsResolved: sumBy(auditAsset, { resolved: true }),
            auditAssetsReissued: sumBy(auditAssets.data, {
                reissuedStatus: enums.status.reissued.reissued
            }),
            assetExcluded: excluded,
            unexpectedAssetsCount: unexpectedAssets.length,
            unexpectedAssetsSeen: sumBy(unexpectedAssets, { seen: true }),

            unexpectedAssetsResolved: sumBy(unexpectedAssets, {
                resolved: true
            }),

            auditors: auditorIds
                .map((id) => {
                    const auditor = helpers.getListItemForId(accounts, id);
                    return auditor ? auditor.displayName : strings("unknown");
                })
                .join(", "),
            controllers: controllerIds
                .map((id) => {
                    const controller = helpers.getListItemForId(accounts, id);
                    return controller
                        ? controller.displayName
                        : strings("unknown");
                })
                .join(", "),
            validatedVisibility: validatedVisibility.length
        };
    }, [accounts, auditAssets, data, assetAuditors]);

    /** Per-controller exclusion counts, mirroring the auditor list above -
     * one row per controller who has excluded at least one asset, with how
     * many they've excluded. A controller can carry more than one
     * AssetController row for the same asset, so dedupe by auditAsset id.
     */
    const controllerExclusionSummary = useMemo(() => {
        const excludedRecords = auditControllers.data.filter((item) =>
            Boolean(item.exclusionReason)
        );
        const byController = groupBy(excludedRecords, "controller");

        return Object.entries(byController).map(([controllerId, records]) => ({
            controller: helpers.getListItemForId(
                accounts,
                Number(controllerId)
            ),
            excludedCount: uniq(records.map((item) => item.auditAsset)).length
        }));
    }, [auditControllers, accounts]);

    /** Summary for donut chart constructing values */
    const donutValues = useMemo(() => {
        const {
            auditAssetUnchecked,
            auditAssetAssigned,
            auditAssetsSeen,
            // auditAssetsPending,
            auditAssetsApproved,
            auditAssetsRejected,
            unexpectedAssetsCount,
            assetExcluded
        } = summaryData;

        return [
            {
                id: 0,
                value: auditAssetUnchecked,
                label: `${strings("unchecked")} ${auditAssetUnchecked}`,
                color: "#b4b8ba",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet([
                            "expectedUnchecked"
                        ])
                    );
                }
            },
            {
                id: 1,
                value: auditAssetsSeen,
                label: `${strings("seen")} ${auditAssetsSeen}`,
                color: "#8eddaf",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet(["expectedSeen"])
                    );
                }
            },
            {
                id: 2,
                value: assetExcluded,
                label: `${strings("excluded")} ${assetExcluded}`,
                color: "#f395aa",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet([
                            "expectedExcluded"
                        ])
                    );
                }
            },
            // {
            //     id: 3,
            //     value: auditAssetsPending,
            //     label: `${strings("pending")} ${auditAssetsPending}`,
            //     color: "#7fbcff",
            //     onClick: () => {
            //         dispatch(
            //             actions.auditReview.create.statusSet([
            //                 "expectedPending"
            //             ])
            //         );
            //     }
            // },
            {
                id: 3,
                value: auditAssetsApproved,
                label: `${strings("approved")} ${auditAssetsApproved}`,
                color: "#007aff",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet([
                            "expectedApproved"
                        ])
                    );
                }
            },
            {
                id: 4,
                value: auditAssetsRejected,
                label: `${strings("rejected")} ${auditAssetsRejected}`,
                color: "#EE315E",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet([
                            "expectedRejected"
                        ])
                    );
                }
            },
            {
                id: 5,
                value: unexpectedAssetsCount,
                label: `${strings("unexpected")} ${unexpectedAssetsCount}`,
                color: "#fdd87d",
                onClick: () => {
                    dispatch(
                        actions.auditReview.create.statusSet(["unexpected"])
                    );
                }
            }
        ];
    }, [summaryData]);
    /** Render circle  for row value which has boolean value */
    const renderCirlce = (data) => {
        const { id, name } = data;
        let color = "grey";
        if (id === 1 || id === 2) color = "yellow";
        if (id === 3) color = "green";

        return (
            <>
                <Typography style={{ fontSize: 0 }}>{name}</Typography>
                <CircleIcon sx={{ width: "0.5em", color: color }} />
            </>
        );
    };

    return {
        auditorAssetStatus,
        controllerExclusionSummary,
        summaryData,
        donutValues,
        data,
        renderCirlce
    };
};
